function dedupeLanes(targets) {
    const seen = new Set();
    const out = [];
    for (const t of targets) {
        if (seen.has(t.clusterId))
            continue;
        seen.add(t.clusterId);
        out.push(t);
    }
    return out;
}
export class Canvas2DRenderer {
    constructor(canvas) {
        Object.defineProperty(this, "canvas", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: canvas
        });
        Object.defineProperty(this, "ctx", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "sparks", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "floats", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "bloom", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "bloomHue", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 200
        });
        Object.defineProperty(this, "dpr", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 1
        });
        Object.defineProperty(this, "detection", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                clusterId: null,
                confidence: 0,
                precision: 0,
            }
        });
        Object.defineProperty(this, "detConfSmooth", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "detActiveSmooth", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "lastDetId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "detPulse", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "trails", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "bgPhase", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "bgImg", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "bgImgReady", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "comboImg", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "comboImgReady", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "comboTier", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "shakeAmt", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "frameHopMs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 23
        });
        Object.defineProperty(this, "countdown", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "onResize", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => this.resize()
        });
        Object.defineProperty(this, "percussiveOf", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => false
        });
        Object.defineProperty(this, "glyphOf", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => "•"
        });
        const c = canvas.getContext("2d");
        if (!c)
            throw new Error("no 2d context");
        this.ctx = c;
        this.resize();
        window.addEventListener("resize", this.onResize);
        this.loadBackdrops();
    }
    setComboTier(tier) {
        this.comboTier = Math.max(0, tier);
    }
    /** Tell the renderer the analysis packet duration for trail sizing. */
    setFrameHopMs(ms) {
        if (Number.isFinite(ms) && ms > 0)
            this.frameHopMs = ms;
    }
    shake(strength = 1) {
        this.shakeAmt = Math.min(1.4, this.shakeAmt + strength);
    }
    announceCountdown(beatsRemaining) {
        const now = typeof performance !== "undefined" ? performance.now() : Date.now();
        const text = beatsRemaining > 0 ? String(beatsRemaining) : "GO!";
        const life = beatsRemaining > 0 ? 700 : 600;
        this.countdown = { text, until: now + life, born: now };
    }
    dispose() {
        window.removeEventListener("resize", this.onResize);
    }
    setGlyphProvider(glyphOf) {
        this.glyphOf = glyphOf;
    }
    setLabelers(glyphOf) {
        this.setGlyphProvider(glyphOf);
    }
    setDetection(d) {
        this.detection = d;
        this.detConfSmooth =
            this.detConfSmooth * 0.7 + (d.clusterId ? d.confidence : 0) * 0.3;
        this.detActiveSmooth =
            this.detActiveSmooth * 0.8 + (d.clusterId ? 1 : 0) * 0.2;
        if (d.clusterId && d.clusterId !== this.lastDetId) {
            this.detPulse = 1;
        }
        this.lastDetId = d.clusterId;
    }
    /**
     * Emit a detection trail line in the lane of a classified cluster. Trails
     * are spawned at the hit line and then propagate along the lane at the same
     * rate as the scrolling targets, giving a visual "echo" of the player's
     * voice moving down the rail alongside the required notes.
     */
    markDetection(clusterId, hue, nowMs) {
        this.trails.push({ clusterId, bornMs: nowMs, hue });
        // Cap the buffer so a long run cannot grow it without bound.
        if (this.trails.length > 240) {
            this.trails.splice(0, this.trails.length - 240);
        }
    }
    resize() {
        this.dpr = Math.min(2, window.devicePixelRatio || 1);
        const w = this.canvas.clientWidth || window.innerWidth;
        const h = this.canvas.clientHeight || window.innerHeight;
        this.canvas.width = Math.floor(w * this.dpr);
        this.canvas.height = Math.floor(h * this.dpr);
    }
    feed(ev) {
        if (!ev)
            return;
        this.bloom = this.bloom * 0.6 + ev.features.energy * 0.4;
        this.bloomHue = 180 + ev.features.centroid * 140;
    }
    spark(laneY, precision, hue, label = "", kind = "hit") {
        const w = this.canvas.width / this.dpr;
        this.sparks.push({
            x: w * 0.28,
            y: laneY,
            r: 6 + precision * 26,
            life: 1,
            hue,
            label,
            kind,
        });
        if (label) {
            this.floats.push({
                x: w * 0.28,
                y: laneY - 34,
                life: 1,
                text: label,
                hue,
            });
        }
    }
    setPercussiveProvider(fn) {
        this.percussiveOf = fn;
    }
    render(targets, laneOf, colorOf, nowMs, timed, lookaheadMs = 1600) {
        const { ctx } = this;
        this.shakeAmt *= 0.86;
        if (this.shakeAmt < 0.01)
            this.shakeAmt = 0;
        let sx = 0;
        let sy = 0;
        if (this.shakeAmt > 0) {
            const mag = this.shakeAmt * 14;
            sx = (Math.random() * 2 - 1) * mag;
            sy = (Math.random() * 2 - 1) * mag;
        }
        ctx.setTransform(this.dpr, 0, 0, this.dpr, sx * this.dpr, sy * this.dpr);
        const w = this.canvas.width / this.dpr;
        const h = this.canvas.height / this.dpr;
        ctx.clearRect(0, 0, w, h);
        this.bgPhase = (this.bgPhase ?? 0) + 0.006;
        const bg = ctx.createLinearGradient(0, 0, w, h);
        bg.addColorStop(0, "#0a0d1c");
        bg.addColorStop(1, "#05060f");
        ctx.fillStyle = bg;
        ctx.fillRect(0, 0, w, h);
        if (this.bgImgReady && this.bgImg) {
            const img = this.bgImg;
            const scale = Math.max(w / img.width, h / img.height) *
                (1.02 + 0.02 * Math.sin(this.bgPhase * 0.5));
            const dw = img.width * scale;
            const dh = img.height * scale;
            ctx.save();
            ctx.globalAlpha = 0.5;
            ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
            ctx.restore();
        }
        const gx = w * (0.5 + 0.28 * Math.sin(this.bgPhase));
        const gy = h * (0.5 + 0.22 * Math.cos(this.bgPhase * 0.8));
        const amb = ctx.createRadialGradient(gx, gy, 0, gx, gy, w * 0.55);
        amb.addColorStop(0, "rgba(34,211,238,0.10)");
        amb.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = amb;
        ctx.fillRect(0, 0, w, h);
        if (this.bloom > 0.02) {
            const g = ctx.createRadialGradient(w * 0.28, h * 0.5, 0, w * 0.28, h * 0.5, w * 0.5 * (0.3 + this.bloom));
            g.addColorStop(0, `hsla(${this.bloomHue}, 90%, 60%, ${0.25 * this.bloom})`);
            g.addColorStop(1, "hsla(0,0%,0%,0)");
            ctx.fillStyle = g;
            ctx.fillRect(0, 0, w, h);
        }
        const hitX = w * 0.28;
        for (const tgt of dedupeLanes(targets)) {
            const y = laneOf(tgt.clusterId);
            const hue = colorOf(tgt.clusterId);
            const laneGrad = ctx.createLinearGradient(0, y, w, y);
            laneGrad.addColorStop(0, `hsla(${hue}, 80%, 60%, 0.02)`);
            laneGrad.addColorStop(0.28, `hsla(${hue}, 80%, 60%, 0.16)`);
            laneGrad.addColorStop(1, `hsla(${hue}, 80%, 60%, 0.04)`);
            ctx.strokeStyle = laneGrad;
            ctx.lineWidth = 34;
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(w, y);
            ctx.stroke();
        }
        const hlPulse = 0.5 + 0.5 * Math.sin(this.bgPhase * 4);
        const hlGrad = ctx.createLinearGradient(hitX - 30, 0, hitX + 30, 0);
        hlGrad.addColorStop(0, "rgba(34,211,238,0)");
        hlGrad.addColorStop(0.5, `rgba(103,232,249,${0.14 + 0.08 * hlPulse})`);
        hlGrad.addColorStop(1, "rgba(34,211,238,0)");
        ctx.fillStyle = hlGrad;
        ctx.fillRect(hitX - 30, 0, 60, h);
        ctx.save();
        ctx.shadowColor = "rgba(103,232,249,0.9)";
        ctx.shadowBlur = 12 + hlPulse * 10;
        ctx.strokeStyle = `rgba(255,255,255,${0.4 + 0.25 * hlPulse})`;
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.moveTo(hitX, 0);
        ctx.lineTo(hitX, h);
        ctx.stroke();
        ctx.restore();
        const pendingBefore = new Map();
        if (!timed) {
            const sorted = [...targets].sort((a, b) => a.order - b.order);
            let acc = 0;
            for (const t of sorted) {
                pendingBefore.set(t.order, acc);
                if (!t.hit)
                    acc += 1;
            }
        }
        for (const tgt of targets) {
            const y = laneOf(tgt.clusterId);
            const hue = colorOf(tgt.clusterId);
            let x;
            if (timed) {
                const dt = tgt.time - nowMs;
                x = hitX + (dt / lookaheadMs) * (w - hitX);
            }
            else {
                const before = pendingBefore.get(tgt.order) ?? 0;
                x = hitX + before * 140 + (tgt.hit ? -80 : 0);
            }
            const r = 22;
            const percussive = this.percussiveOf(tgt.clusterId);
            const rectW = percussive ? 0 : Math.max(44, (tgt.sustainMs ?? 0) * 0.12);
            const leftEdge = x - rectW / 2;
            const rightEdge = x + rectW / 2;
            if (rightEdge < -60 || leftEdge > w + 60)
                continue;
            if (timed && !tgt.hit) {
                const dt = Math.abs(tgt.time - nowMs);
                if (dt < 400) {
                    const glow = 1 - dt / 400;
                    ctx.strokeStyle = `hsla(${hue}, 90%, 70%, ${0.6 * glow})`;
                    ctx.lineWidth = 3;
                    ctx.beginPath();
                    if (percussive) {
                        ctx.arc(x, y, r + (6 + glow * 10), 0, Math.PI * 2);
                    }
                    else {
                        const pad = 6 + glow * 10;
                        ctx.rect(x - rectW / 2 - pad, y - r - pad, rectW + pad * 2, r * 2 + pad * 2);
                    }
                    ctx.stroke();
                }
            }
            ctx.fillStyle = tgt.hit
                ? tgt.missed
                    ? `hsla(${hue}, 20%, 50%, 0.18)`
                    : `hsla(${hue}, 80%, 60%, 0.22)`
                : `hsla(${hue}, 80%, 60%, 0.9)`;
            ctx.beginPath();
            if (percussive) {
                ctx.arc(x, y, r, 0, Math.PI * 2);
            }
            else {
                const rr = 10;
                this.roundRect(x - rectW / 2, y - r, rectW, r * 2, rr);
            }
            ctx.fill();
            if (!percussive && tgt.sustainMs > 0 && tgt.hit) {
                // Fill reflects coverage against the (loosened) required
                // threshold rather than the full window, so the bar reads full
                // exactly when the sustain is satisfied.
                const required = (tgt.sustainMs || 1) * 0.6;
                const held = Math.max(0, Math.min(1, (tgt.heldMs ?? 0) / required));
                if (held > 0) {
                    ctx.fillStyle = `hsla(${hue}, 95%, 72%, 0.85)`;
                    const fillW = rectW * held;
                    const rr = 10;
                    this.roundRect(x - rectW / 2, y - r, Math.max(fillW, 1), r * 2, rr);
                    ctx.fill();
                }
            }
            const glyph = this.glyphOf(tgt.clusterId);
            ctx.font = `700 18px system-ui, sans-serif`;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            this.strokedText(glyph, x, y, tgt.hit ? "rgba(255,255,255,0.55)" : "rgba(255,255,255,0.98)", 4);
        }
        // Detection trails: propagate down each lane from the hit line at the
        // same pixels-per-ms rate as the scrolling targets.
        const rate = (w - hitX) / (lookaheadMs || 1);
        this.trails = this.trails.filter((tr) => {
            const age = nowMs - tr.bornMs;
            const x = hitX - age * rate;
            return x > -40;
        });
        for (const tr of this.trails) {
            const y = laneOf(tr.clusterId);
            const age = nowMs - tr.bornMs;
            const x = hitX - age * rate;
            const fade = Math.max(0, 1 - age / (lookaheadMs * 1.5));
            ctx.save();
            ctx.strokeStyle = `hsla(${tr.hue}, 95%, 68%, ${0.55 * fade})`;
            // Width proportional to a single analysis packet so each raw
            // classifier frame reads as a solid slice on the rail.
            ctx.lineWidth = Math.max(3, rate * this.frameHopMs);
            ctx.shadowColor = `hsla(${tr.hue}, 95%, 60%, ${0.8 * fade})`;
            ctx.shadowBlur = 8 * fade;
            ctx.beginPath();
            ctx.moveTo(x, y - 17);
            ctx.lineTo(x, y + 17);
            ctx.stroke();
            ctx.restore();
        }
        this.renderDetection(hitX, h, laneOf, colorOf);
        this.sparks = this.sparks.filter((s) => s.life > 0);
        for (const s of this.sparks) {
            ctx.globalAlpha = s.life;
            ctx.strokeStyle =
                s.kind === "miss"
                    ? `hsla(${s.hue}, 20%, 70%, 0.8)`
                    : `hsl(${s.hue}, 90%, 65%)`;
            ctx.lineWidth = s.kind === "miss" ? 2 : 3;
            ctx.beginPath();
            ctx.arc(s.x, s.y, s.r * (1.5 - s.life), 0, Math.PI * 2);
            ctx.stroke();
            s.life -= 0.05;
        }
        ctx.globalAlpha = 1;
        this.floats = this.floats.filter((f) => f.life > 0);
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        for (const f of this.floats) {
            ctx.globalAlpha = Math.min(1, f.life * 1.4);
            ctx.font = `800 16px system-ui, sans-serif`;
            this.strokedText(f.text, f.x, f.y - (1 - f.life) * 28, `hsl(${f.hue}, 90%, 72%)`, 3.5);
            f.life -= 0.03;
        }
        ctx.globalAlpha = 1;
        if (this.comboTier > 0 && this.comboImgReady && this.comboImg) {
            const tier = Math.min(3, this.comboTier);
            const base = 0.1 + tier * 0.12;
            const pulse = 0.85 + 0.15 * Math.sin(this.bgPhase * 3);
            const cx = hitX;
            const cy = h * 0.5;
            const rad = Math.max(w, h) * (0.5 + tier * 0.12) * pulse;
            ctx.save();
            ctx.globalCompositeOperation = "screen";
            ctx.globalAlpha = base * pulse;
            ctx.drawImage(this.comboImg, cx - rad, cy - rad, rad * 2, rad * 2);
            ctx.restore();
            ctx.globalAlpha = 1;
        }
        this.drawCountdown(w, h);
    }
    loadBackdrops() {
        const bg = new Image();
        bg.onload = () => {
            this.bgImg = bg;
            this.bgImgReady = true;
        };
        bg.onerror = () => console.warn("[Canvas2DRenderer] play-field backdrop failed to load");
        bg.src = "/images/playfield.png";
        const combo = new Image();
        combo.onload = () => {
            this.comboImg = combo;
            this.comboImgReady = true;
        };
        combo.onerror = () => console.warn("[Canvas2DRenderer] combo bloom failed to load");
        combo.src = "/images/combo-bloom.png";
    }
    strokedText(text, x, y, fill, strokeW) {
        const { ctx } = this;
        ctx.lineJoin = "round";
        ctx.miterLimit = 2;
        ctx.lineWidth = strokeW;
        ctx.strokeStyle = "rgba(0,0,0,0.85)";
        ctx.strokeText(text, x, y);
        ctx.fillStyle = fill;
        ctx.fillText(text, x, y);
    }
    drawCountdown(w, h) {
        if (!this.countdown)
            return;
        const { ctx } = this;
        const now = typeof performance !== "undefined" ? performance.now() : Date.now();
        if (now >= this.countdown.until) {
            this.countdown = null;
            return;
        }
        const life = (this.countdown.until - now) /
            (this.countdown.until - this.countdown.born);
        const age = 1 - life;
        const isGo = this.countdown.text === "GO!";
        const scale = age < 0.2 ? 0.6 + (age / 0.2) * 0.5 : 1.1 - Math.min(0.1, age - 0.2);
        const alpha = life > 0.3 ? 1 : life / 0.3;
        const color = isGo ? "#4ade80" : "#22d3ee";
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(w / 2, h * 0.42);
        ctx.scale(scale, scale);
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.font = `900 ${isGo ? 96 : 120}px system-ui, sans-serif`;
        ctx.shadowColor = color;
        ctx.shadowBlur = 40;
        ctx.lineWidth = 8;
        ctx.lineJoin = "round";
        ctx.strokeStyle = "rgba(0,0,0,0.7)";
        ctx.strokeText(this.countdown.text, 0, 0);
        ctx.fillStyle = color;
        ctx.fillText(this.countdown.text, 0, 0);
        ctx.restore();
        ctx.globalAlpha = 1;
    }
    roundRect(x, y, w, h, r) {
        const { ctx } = this;
        const rad = Math.min(r, w / 2, h / 2);
        ctx.beginPath();
        ctx.moveTo(x + rad, y);
        ctx.arcTo(x + w, y, x + w, y + h, rad);
        ctx.arcTo(x + w, y + h, x, y + h, rad);
        ctx.arcTo(x, y + h, x, y, rad);
        ctx.arcTo(x, y, x + w, y, rad);
        ctx.closePath();
    }
    renderDetection(hitX, h, laneOf, colorOf) {
        const { ctx } = this;
        const d = this.detection;
        if (this.detPulse > 0)
            this.detPulse = Math.max(0, this.detPulse - 0.04);
        const barH = h * 0.5;
        const barY = h * 0.25;
        const barX = 16;
        const barW = 8;
        ctx.fillStyle = "rgba(255,255,255,0.12)";
        ctx.fillRect(barX, barY, barW, barH);
        const lvl = Math.max(0, Math.min(1, this.detConfSmooth));
        const hue = d.clusterId ? colorOf(d.clusterId) : 0;
        ctx.fillStyle = d.clusterId
            ? `hsl(${hue}, 85%, 60%)`
            : "rgba(255,255,255,0.2)";
        ctx.fillRect(barX, barY + barH * (1 - lvl), barW, barH * lvl);
        if (d.clusterId && this.detActiveSmooth > 0.03) {
            const y = laneOf(d.clusterId);
            const present = Math.max(0, Math.min(1, this.detActiveSmooth));
            const laneThickness = 30;
            const boxW = 52;
            const pulse = this.detPulse;
            const intensity = (0.25 + 0.75 * lvl) * present;
            const cellX = hitX - boxW / 2;
            const cellY = y - laneThickness / 2;
            ctx.save();
            ctx.shadowColor = `hsla(${hue}, 95%, 60%, ${0.9 * intensity})`;
            ctx.shadowBlur = 6 + pulse * 6;
            ctx.fillStyle = `hsla(${hue}, 95%, 62%, ${0.55 + 0.45 * intensity})`;
            ctx.fillRect(cellX, cellY, boxW, laneThickness);
            ctx.shadowBlur = 3 + pulse * 4;
            ctx.fillStyle = `hsla(${hue}, 100%, 78%, ${0.4 + 0.5 * intensity})`;
            const coreInset = 6;
            ctx.fillRect(cellX + coreInset, cellY + coreInset, Math.max(0, boxW - coreInset * 2), Math.max(0, laneThickness - coreInset * 2));
            ctx.restore();
            ctx.strokeStyle = `hsla(${hue}, 100%, 82%, ${0.7 * intensity + 0.3 + pulse * 0.3})`;
            ctx.lineWidth = 2 + pulse * 2;
            ctx.strokeRect(cellX, cellY, boxW, laneThickness);
            const glyph = this.glyphOf(d.clusterId);
            ctx.font = `800 18px system-ui, sans-serif`;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            this.strokedText(glyph, hitX, y, `hsla(${hue}, 100%, 92%, ${0.7 + 0.3 * intensity})`, 4);
        }
    }
}
